import React, { useState } from 'react';
import { VerifierSelectInputRecord } from 'types/dynamic-forms/records/VerifierSelectInputRecord';
import usePropertyPath from '../../hooks/usePropertyPath';
import useProperty from '../../hooks/useProperty';
import WizardSelectControlledComponent from 'components/v2/epd-wizard-inputs/WizardSelectControlled';
import useDataSource from '../../hooks/useDataSource';
import { VerifierType } from 'types/types';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import WizardAddButton from 'components/v2/epd-wizard-inputs/WizardAddButton';
import { useDocumentStore } from '../../hooks/useDocumentStore';
import VerifierSelectInputRendererDialog from './VerifierSelectInputRendererDialog';
import { AddVerifierToCompanyModel } from 'types/dynamic-forms/companies/AddVerifierToCompanyModel';
import { postAddVerifierToCompany } from 'services/api/dynamicFormsCompaniesApi';
import { formatCompanyAddress } from 'util/utils';

const VerifierSelectInputRenderer: React.FC<VerifierSelectInputRecord> = ({ propertyId, label, tooltip, readOnly }) => {
  const store = useDocumentStore();
  const verifierUserPropertyPath = usePropertyPath(propertyId);
  const { value, setValue, errorMessage, isDisabled, isVisible } = useProperty<string | null>(verifierUserPropertyPath);
  const { options, invalidateDataSource } = useDataSource(verifierUserPropertyPath);
  const [showSelectDialog, setShowSelectDialog] = useState<boolean>(false);

  if (!isVisible) {
    return null;
  }

  const handleAddVerifier = async (model: AddVerifierToCompanyModel) => {
    const addResult = await postAddVerifierToCompany(store.metadata.companyId, model);
    await invalidateDataSource();
    setValue(addResult.userId);
  };

  const currentOption = options.find((o) => o.value === value);
  const verifierLabel = label ?? 'EPD Verifier';
  const verifierTooltip =
    tooltip ??
    'The third-party verifier responsible for verifying this EPD. The EPD Owner can manage available verifiers and add new verifiers.';
  const verifierDisplayValue =
    currentOption?.label ?? value ?? 'Please select the EPD Verifier in Step 1 - General information';

  return (
    <>
      {!readOnly && (
        <div>
          <WizardSelectControlledComponent
            name={propertyId}
            options={options}
            value={options.find((o) => o.value === value)}
            label={verifierLabel}
            tooltip={verifierTooltip}
            onChanged={(_: string, value: any) => setValue(value ?? null)}
            error={errorMessage}
            disabled={isDisabled}
          />

          {!isDisabled && <WizardAddButton label="+ Add EPD Verifier" onClick={() => setShowSelectDialog(true)} />}
        </div>
      )}

      {!readOnly && (
        <VerifierSelectInputRendererDialog
          isOpened={showSelectDialog}
          onHide={() => setShowSelectDialog(false)}
          companyId={store.metadata.companyId}
          onAddVerifier={handleAddVerifier}
        />
      )}

      {readOnly && (
        <WizardTextInputControlledComponent
          label={verifierLabel}
          tooltip={verifierTooltip}
          name={`${propertyId}-readonly`}
          value={verifierDisplayValue}
          error={errorMessage}
          disabled
        />
      )}

      {!readOnly && currentOption?.meta && (
        <>
          {currentOption?.meta.type === VerifierType.Body && (
            <>
              <WizardTextInputControlledComponent
                label="Accredited certification body address"
                name="acbAddress"
                value={formatCompanyAddress(
                  {
                    country: currentOption.meta.country,
                    city: currentOption.meta.city ?? null,
                    zipCode: currentOption.meta.zipCode ?? null,
                    addressLine: currentOption.meta.addressLine ?? null,
                  },
                  currentOption.meta.country
                )}
                disabled
              />
              <WizardTextInputControlledComponent
                label="Certification body accredited by"
                name="acbAccreditedBy"
                value={currentOption.meta.approvedBy}
                disabled
              />
            </>
          )}
          {currentOption?.meta.type === VerifierType.Individual && (
            <WizardTextInputControlledComponent
              label="Individual verifier approved by"
              name="verifierApprovedBy"
              value={currentOption.meta.approvedBy}
              disabled
            />
          )}
        </>
      )}
    </>
  );
};

export default VerifierSelectInputRenderer;
